import { useEffect, useMemo, useRef } from "react";
import { buildCardVideoMiniPlayerMeta } from "./cardVideoMiniPlayerMeta";
import {
  detachCardVideoElementForNavigation,
  detachCardYoutubeEmbedForNavigation,
  playExclusiveCardMedia,
  registerCardVideoElement,
  registerCardYoutubeEmbed,
} from "./singleCardMedia";

type ManagedVideoOptions = {
  mediaUrl?: string;
  title?: string;
  description?: string;
  subtitle?: string;
  posterUrl?: string;
  enabled?: boolean;
};

/** File / native video — register, exclusive play, mini-player handoff on unmount. */
export function useManagedCardFileVideo(options: ManagedVideoOptions) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const enabled = options.enabled !== false && Boolean(options.mediaUrl);

  const miniPlayerMeta = useMemo(
    () =>
      buildCardVideoMiniPlayerMeta({
        title: options.title,
        description: options.description,
        subtitle: options.subtitle,
        mediaUrl: options.mediaUrl,
        posterUrl: options.posterUrl,
      }),
    [
      options.title,
      options.description,
      options.subtitle,
      options.mediaUrl,
      options.posterUrl,
    ],
  );

  useEffect(() => {
    if (!enabled) return;
    const el = videoRef.current;
    if (!el) return;
    registerCardVideoElement(el);
    const onPlay = () => {
      void playExclusiveCardMedia(el).catch(() => {});
    };
    el.addEventListener("play", onPlay);
    return () => {
      el.removeEventListener("play", onPlay);
      detachCardVideoElementForNavigation(el, miniPlayerMeta);
    };
  }, [enabled, options.mediaUrl, miniPlayerMeta]);

  return { videoRef, miniPlayerMeta };
}

type ManagedYoutubeOptions = {
  embedUrl?: string;
  sourceUrl?: string;
  title?: string;
  description?: string;
  subtitle?: string;
  enabled?: boolean;
};

/** YouTube iframe — register, mini-player handoff when leaving while playing. */
export function useManagedCardYoutubeEmbed(options: ManagedYoutubeOptions) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const enabled = options.enabled !== false && Boolean(options.embedUrl);

  const miniPlayerMeta = useMemo(
    () =>
      buildCardVideoMiniPlayerMeta({
        title: options.title,
        description: options.description,
        subtitle: options.subtitle,
        mediaUrl: options.sourceUrl || options.embedUrl,
        embedUrl: options.embedUrl,
      }),
    [
      options.title,
      options.description,
      options.subtitle,
      options.sourceUrl,
      options.embedUrl,
    ],
  );

  useEffect(() => {
    if (!enabled) return;
    const iframe = iframeRef.current;
    if (!iframe) return;
    registerCardYoutubeEmbed(iframe, options.sourceUrl || options.embedUrl);
    return () => {
      detachCardYoutubeEmbedForNavigation(iframe, miniPlayerMeta);
    };
  }, [enabled, options.embedUrl, options.sourceUrl, miniPlayerMeta]);

  return { iframeRef, miniPlayerMeta };
}
